"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import type { FeedPostId } from "@/contexts/feed/domain/feed-enums";
import { feedSpotlightEq, FeedSpotlightRpc } from "./api/feed-spotlight-client";
import { spotlightKeys } from "./keys";

/** The caller's coach mark for a post they opened from a spotlight, or null. */
export function useSpotlightCoach(postId: string | null) {
  const {
    data
  } = useQuery({
    ...feedSpotlightEq.queryOptions({
      queryKey: spotlightKeys.coach(postId ?? "none"),
      queryFn: () => Effect.gen(function* () {
        const rpc = yield* RpcClient.make(FeedSpotlightRpc);
        return yield* rpc.getSpotlightCoachMark({
          postId: postId as FeedPostId
        });
      }).pipe(Effect.scoped),
      staleTime: Infinity
    }),
    enabled: postId !== null,
    refetchOnWindowFocus: false,
    retry: false
  });
  return data ?? null;
}
export function useDismissSpotlightCoach() {
  const qc = useQueryClient();
  return useMutation({
    ...feedSpotlightEq.mutationOptions({
      mutationKey: ["feed-spotlight", "dismiss-coach"],
      mutationFn: (v: {
        postId: string;
        version: number;
      }) => Effect.gen(function* () {
        const rpc = yield* RpcClient.make(FeedSpotlightRpc);
        return yield* rpc.dismissSpotlightCoachMark({
          postId: v.postId as FeedPostId,
          version: v.version
        });
      }).pipe(Effect.scoped)
    }),
    // Retired for good either way: the mark is gone from this page now, and a
    // failed dismissal at worst shows it once more on a later landing.
    onSettled: (_data, _error, v_0) => qc.setQueryData(spotlightKeys.coach(v_0.postId), null)
  });
}